import { Suspense, lazy, useCallback, useContext, useState } from "react";
import { OptionsChain } from "../../components/OptionsChain";
import OptionsTradeForm from "../../components/TradeForm/OptionsTradeForm";
import WatchlistTickerStrip from "../../components/WatchlistTickerStrip";
import { MarketContext } from "../../contexts/MarketContext";
import {
  HeaderSection,
  MarketSelectionWrapper,
  OptionsChainSection,
  OptionsSection,
  PerpsSection,
  TickerStripRow,
  TradeFormWrapper,
  TradesSection,
  TradingPageContent,
  TradingPageWrapper,
} from "./style";

import { InstrumentTypeResponse, OptionTypeResponse } from "../../codegen-api";
import MarketSelectionButton from "../../components/MarketSelectionButton";
import OptionsHistory from "../../components/OptionsHistory";
import PerpsOrderbookAndChart from "../../components/Perps/PerpsOrderbookAndChart";
import PerpsTradeForm, {
  IPerpsFormDefaultValues,
} from "../../components/TradeForm/PerpsTradeForm";
import LazyOptionsTradingHeader from "../../components/TradingHeader/LazyOptionsTradingHeader";
import LazyPerpsTradingHeader from "../../components/TradingHeader/LazyPerpsTradingHeader";
import { volatileAssets } from "../../constants/assets";
import { COMPONENTS } from "../../constants/design/spacing";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import useScreenSize from "../../hooks/screenSize/useScreenSize";
import usePersistentState from "../../hooks/usePersistentState";
import { AssetResponse } from "../../utils/asset";
import TradingPageTabs, {
  TradingPageTabEnum,
  TRADING_PAGE_TAB_SLUGS,
} from "./TradingPageTabs";
import { useUrlTabState } from "../../hooks/useUrlParamState";
import VolatilityWarningBanner from "./VolatilityWarningBanner";
import { useResetOnMarketSwitch } from "../../components/TradeForm/PerpsTradeForm/form.useResetOnMarketSwitch";

// Lazy components
const LazyTradingBottomBar = lazy(
  () => import("../../components/TradingBottomBar")
);

function TradingPage() {
  const { market } = useContext(MarketContext);
  const {
    setOrderType,
    setExpiry,
    setStrike,
    selectedOptionInstrument,
    selectedPerpetualInstrument,
    selectedSpotInstrument,
  } = useContext(MarketInstrumentContext);

  const { isMobileScreen } = useScreenSize();
  const {
    showOnlyCurrentMarket,
    setShowOnlyCurrentMarket,
    showBottomTray: bottomBarCollapsed,
    setShowBottomTray: setBottomBarCollapsed,
  } = usePersistentState();

  // Deep-linkable as `?view=<slug>` (options-chain | trade-history).
  const [currentTab, setCurrentTab] = useUrlTabState<TradingPageTabEnum>(
    "view",
    TradingPageTabEnum.OPTIONS_CHAIN,
    TRADING_PAGE_TAB_SLUGS
  );
  const [perpDefaultValues, setPerpDefaultValues] =
    useState<IPerpsFormDefaultValues>();

  const onTrade = useCallback(
    (orderType: OptionTypeResponse, expiry: number, strike: string) => {
      setOrderType(orderType);
      setExpiry(expiry);
      setStrike(strike);
    },
    [setExpiry, setOrderType, setStrike]
  );

  const onPerpOrderbookRowClick = useCallback((price: string) => {
    setPerpDefaultValues({ price });
  }, []);

  // An orderbook price belongs to the market it was clicked on. The form only reads
  // it on a change of identity, so it is harmless while mounted -- but a remount
  // after a switch (e.g. crossing the mobile breakpoint) would re-apply the old
  // market's price as if it were fresh.
  useResetOnMarketSwitch(selectedPerpetualInstrument?.instrument_id, () =>
    setPerpDefaultValues(undefined)
  );

  const onToggleBottomBar = useCallback(() => {
    setBottomBarCollapsed((prev) => !prev);
  }, [setBottomBarCollapsed]);

  return (
    <>
      {volatileAssets.includes(
        selectedPerpetualInstrument?.underlying_asset as AssetResponse
      ) && (
        <VolatilityWarningBanner
          dismissKey={selectedPerpetualInstrument?.underlying_asset}
        />
      )}
      <TradingPageWrapper>
        <HeaderSection>
          <MarketSelectionButton />
          <MarketSelectionWrapper>
            {market.derivative === InstrumentTypeResponse.Option && (
              <LazyOptionsTradingHeader isMobileScreen={isMobileScreen} />
            )}
            {market.derivative === InstrumentTypeResponse.Perpetual && (
              <LazyPerpsTradingHeader
                perpInstrument={selectedPerpetualInstrument}
              />
            )}
            {market.derivative === InstrumentTypeResponse.Spot && (
              <LazyPerpsTradingHeader
                perpInstrument={selectedSpotInstrument}
              />
            )}
          </MarketSelectionWrapper>
        </HeaderSection>
        <TickerStripRow>
          <WatchlistTickerStrip />
        </TickerStripRow>
        {market.derivative === InstrumentTypeResponse.Option && (
          <OptionsSection
            bottomCollapsed={bottomBarCollapsed}
            bottomCollapsedHeight={`${COMPONENTS.tradeBottomBar.header}px`}
          >
            <OptionsChainSection>
              <TradingPageTabs
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
              />
              <TradingPageContent>
                {currentTab === TradingPageTabEnum.OPTIONS_CHAIN ? (
                  <OptionsChain
                    onTrade={onTrade}
                    activeInstrumentId={
                      selectedOptionInstrument?.instrument_name
                    }
                  />
                ) : (
                  <OptionsHistory />
                )}
              </TradingPageContent>
            </OptionsChainSection>

            <Suspense>
              <LazyTradingBottomBar
                collapsed={bottomBarCollapsed}
                onToggleCollapse={onToggleBottomBar}
                showOnlyCurrentMarket={showOnlyCurrentMarket}
                setShowOnlyCurrentMarket={setShowOnlyCurrentMarket}
              />
            </Suspense>
          </OptionsSection>
        )}
        {market.derivative === InstrumentTypeResponse.Perpetual && (
          <PerpsSection
            bottomCollapsed={bottomBarCollapsed}
            bottomCollapsedHeight={`${COMPONENTS.tradeBottomBar.header}px`}
          >
            <PerpsOrderbookAndChart
              onOrderbookRowClick={onPerpOrderbookRowClick}
              perpInstrument={selectedPerpetualInstrument}
            />
            <Suspense>
              <LazyTradingBottomBar
                collapsed={bottomBarCollapsed}
                onToggleCollapse={onToggleBottomBar}
                style={{ gridColumn: "1 / span 2" }}
                showOnlyCurrentMarket={showOnlyCurrentMarket}
                setShowOnlyCurrentMarket={setShowOnlyCurrentMarket}
              />
            </Suspense>
          </PerpsSection>
        )}
        {market.derivative === InstrumentTypeResponse.Spot && (
          <PerpsSection
            bottomCollapsed={bottomBarCollapsed}
            bottomCollapsedHeight={`${COMPONENTS.tradeBottomBar.header}px`}
          >
            <PerpsOrderbookAndChart
              onOrderbookRowClick={onPerpOrderbookRowClick}
              perpInstrument={selectedSpotInstrument}
              instrumentType={InstrumentTypeResponse.Spot}
            />
            <Suspense>
              <LazyTradingBottomBar
                collapsed={bottomBarCollapsed}
                onToggleCollapse={onToggleBottomBar}
                style={{ gridColumn: "1 / span 2" }}
                showOnlyCurrentMarket={showOnlyCurrentMarket}
                setShowOnlyCurrentMarket={setShowOnlyCurrentMarket}
              />
            </Suspense>
          </PerpsSection>
        )}
        <TradesSection>
          <TradeFormWrapper>
            {market.derivative === InstrumentTypeResponse.Option && (
              <OptionsTradeForm selectedInstrument={selectedOptionInstrument} />
            )}
            {(market.derivative === InstrumentTypeResponse.Perpetual ||
              market.derivative === InstrumentTypeResponse.Spot) && (
              <PerpsTradeForm
                defaultValues={perpDefaultValues}
                perpInstrument={
                  market.derivative === InstrumentTypeResponse.Spot
                    ? selectedSpotInstrument
                    : selectedPerpetualInstrument
                }
                tradingType={
                  market.derivative === InstrumentTypeResponse.Spot
                    ? "spot"
                    : "perp"
                }
              />
            )}
          </TradeFormWrapper>
        </TradesSection>
      </TradingPageWrapper>
    </>
  );
}
export default TradingPage;
